import { QRCode } from "react-qr-code";
import {
  MdContentCopy,
  MdDownload,
  MdEmail,
  MdOpenInNew,
  MdQrCode,
  MdSend,
  MdSms,
} from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import { InputField, Select, Textarea } from "components/fields";
import ChoiceCard from "components/heartbeat/ChoiceCard";
import OptionGroup from "components/heartbeat/OptionGroup";

const DELIVERY_METHODS = [
  { value: "email", label: "Email Only", icon: MdEmail },
  { value: "sms", label: "SMS Only", icon: MdSms },
  { value: "qr_link", label: "QR Code / Link", icon: MdQrCode },
];

export const formatHour = (i) => (i === 0 ? "12 AM" : i === 12 ? "12 PM" : i < 12 ? `${i} AM` : `${i - 12} PM`);

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({ value: i, label: formatHour(i) }));

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
];

export const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const PANEL_CLASS = "rounded-control bg-surface-raised p-4";

const DeliveryWindow = ({ formData, handleChange }) => (
  <section className={`${PANEL_CLASS} space-y-4`}>
    <div>
      <h3 className="font-semibold text-fg">Delivery Time Window</h3>
      <p className="mt-1 text-sm text-fg-muted">
        Set the hours during which surveys can be sent. This prevents surveys from being sent
        during church services or late at night.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2">
      <Select
        label="Start Time"
        value={formData.deliveryWindowStart}
        onChange={(e) => handleChange("deliveryWindowStart", parseInt(e.target.value))}
        options={HOUR_OPTIONS}
      />
      <Select
        label="End Time"
        value={formData.deliveryWindowEnd}
        onChange={(e) => handleChange("deliveryWindowEnd", parseInt(e.target.value))}
        options={HOUR_OPTIONS}
      />
    </div>

    <Select
      label="Timezone"
      value={formData.organizationTimezone}
      onChange={(e) => handleChange("organizationTimezone", e.target.value)}
      options={TIMEZONE_OPTIONS}
    />

    <OptionGroup
      label="Send Days"
      hint="Restrict sending to specific days. Leave all unselected to send any day."
    >
      <div className="flex flex-wrap gap-2">
        {WEEK_DAYS.map((day, i) => {
          const active = !!formData.scheduleDays?.includes(i);
          return (
            <Button
              key={day}
              size="sm"
              variant={active ? "primary" : "secondary"}
              aria-pressed={active}
              onClick={() => {
                const current = formData.scheduleDays || [];
                const updated = active
                  ? current.filter((d) => d !== i)
                  : [...current, i].sort((a, b) => a - b);
                handleChange("scheduleDays", updated.length === 0 ? null : updated);
              }}
            >
              {day}
            </Button>
          );
        })}
      </div>
    </OptionGroup>

    <p className="text-sm text-fg-muted">
      Example: If set to 9 AM - 8 PM Central on Mon–Fri, and a member is added on Saturday, the
      survey will be queued and sent Monday at 9 AM.
    </p>
  </section>
);

const QrCodePanel = ({ editor }) => {
  const { isEdit, formData, qrRef, copyToClipboard, downloadQRCode, generatingQr, handleGenerateQrCode } =
    editor;
  const surveyUrl = formData.qrCodeShortUrl || formData.qrCodeUrl;

  return (
    <section className={`${PANEL_CLASS} space-y-4`}>
      <div className="flex items-center gap-2">
        <MdQrCode aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
        <h3 className="font-semibold text-fg">QR Code & Direct Link</h3>
      </div>

      {!isEdit ? (
        <p className="py-4 text-center text-sm text-fg-muted">
          Save this campaign first to generate a QR code and shareable link.
        </p>
      ) : formData.qrCodeToken ? (
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <InputField
              label={`Survey Link${formData.qrCodeShortUrl ? " (Short URL)" : ""}`}
              className="flex-1"
              readOnly
              value={surveyUrl}
            />
            <Button
              variant="secondary"
              leftIcon={<MdContentCopy aria-hidden="true" className="h-4 w-4" />}
              onClick={() => copyToClipboard(surveyUrl, "Survey link")}
            >
              Copy
            </Button>
          </div>

          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <div ref={qrRef} className="shrink-0 rounded-control border border-line bg-white p-3">
              <QRCode value={surveyUrl} size={120} level="H" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="mb-3 text-sm text-fg-muted">
                Display this QR code in your lobby, bulletins, or presentation slides. Visitors can
                scan it to take the survey instantly.
              </p>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<MdDownload aria-hidden="true" className="h-4 w-4" />}
                onClick={downloadQRCode}
              >
                Download QR Code
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <p className="text-sm text-fg-muted">
            Generate a QR code to allow visitors to take this survey without needing an email
            invitation.
          </p>
          <Button
            variant="secondary"
            loading={generatingQr}
            leftIcon={<MdQrCode aria-hidden="true" className="h-4 w-4" />}
            onClick={handleGenerateQrCode}
          >
            {generatingQr ? "Generating…" : "Generate QR Code"}
          </Button>
        </div>
      )}
    </section>
  );
};

const PreviewAndSend = ({ editor }) => {
  const { formData, generatingPreview, handlePreviewSurvey, sendingNow, handleSendNow } = editor;

  return (
    <section className={`${PANEL_CLASS} divide-y divide-line`}>
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
        <div>
          <h3 className="font-semibold text-fg">Preview Survey</h3>
          <p className="mt-1 text-sm text-fg-muted">
            Open the survey in a new tab to see how it looks to recipients
          </p>
        </div>
        <Button
          variant="secondary"
          loading={generatingPreview}
          leftIcon={<MdOpenInNew aria-hidden="true" className="h-4 w-4" />}
          onClick={handlePreviewSurvey}
        >
          {generatingPreview ? "Opening…" : "Preview Survey"}
        </Button>
      </div>

      {formData.deliveryMethod !== "qr_link" && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
          <div>
            <h3 className="font-semibold text-fg">Send Now</h3>
            <p className="mt-1 text-sm text-fg-muted">
              Immediately send this survey to all eligible recipients
            </p>
          </div>
          <Button
            variant="secondary"
            loading={sendingNow}
            leftIcon={<MdSend aria-hidden="true" className="h-4 w-4" />}
            onClick={handleSendNow}
          >
            {sendingNow ? "Sending…" : "Send Now"}
          </Button>
        </div>
      )}
    </section>
  );
};

const DeliverySection = ({ editor }) => {
  const { isEdit, formData, handleChange } = editor;

  return (
    <Card padding="md" className="gap-6">
      <CardHeader title="Delivery Settings" />
      <OptionGroup label="Delivery Method">
        <div className="grid gap-4 sm:grid-cols-3">
          {DELIVERY_METHODS.map((method) => (
            <ChoiceCard
              key={method.value}
              selected={formData.deliveryMethod === method.value}
              onClick={() => handleChange("deliveryMethod", method.value)}
              icon={method.icon}
              title={method.label}
            />
          ))}
        </div>
      </OptionGroup>

      {formData.deliveryMethod === "email" && (
        <div className="space-y-4">
          <InputField
            label="Email Subject"
            value={formData.emailSubject}
            onChange={(e) => handleChange("emailSubject", e.target.value)}
            placeholder="Enter email subject..."
          />
          <Textarea
            label="Email Body"
            rows={4}
            value={formData.emailBody}
            onChange={(e) => handleChange("emailBody", e.target.value)}
            placeholder="Enter email message..."
            hint="The survey link will be automatically added to the email."
          />
        </div>
      )}

      {formData.deliveryMethod === "sms" && (
        <Textarea
          label="SMS Message"
          rows={3}
          value={formData.smsMessage}
          onChange={(e) => handleChange("smsMessage", e.target.value)}
          placeholder="Enter SMS message..."
          hint={`Use {{survey_link}} to include the survey link. Max 160 characters recommended.`}
        />
      )}

      {formData.deliveryMethod !== "qr_link" && (
        <DeliveryWindow formData={formData} handleChange={handleChange} />
      )}

      {formData.deliveryMethod === "qr_link" && <QrCodePanel editor={editor} />}

      {isEdit && <PreviewAndSend editor={editor} />}
    </Card>
  );
};

export default DeliverySection;
